import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


const platformApi =
    new API({
        tokenKey:
            "platformAccessToken",
    });


const initialState = {

    plans: [],

    modules: [],

    legacyModuleCodeMap: {},

    modulesLoading: false,

    modulesError: null,

    syncingTenants: false,

    syncTenantsError: null,

    syncTenantsResult: null,

    selectedPlan: null,

    loading: false,

    detailsLoading: false,

    saving: false,

    deleting: false,

    error: null,
};


/*
|--------------------------------------------------------------------------
| DYNAMIC SUBSCRIPTION MODULES
|--------------------------------------------------------------------------
*/

export const fetchPlatformSubscriptionModules =
    createAsyncThunk(
        "platformSubscriptionPlan/modules",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.get(
                    config
                        .platformSubscriptionModules
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| SYNC ALL CURRENT TENANT PLAN ACCESS
|--------------------------------------------------------------------------
*/

export const syncPlatformTenantPlanAccess =
    createAsyncThunk(
        "platformSubscriptionPlan/syncTenants",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.post(
                    config
                        .syncPlatformTenantPlanAccess,
                    {}
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| LIST
|--------------------------------------------------------------------------
*/

export const fetchPlatformSubscriptionPlans =
    createAsyncThunk(
        "platformSubscriptionPlan/list",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.get(
                    config.platformSubscriptionPlans,
                    params
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| DETAILS
|--------------------------------------------------------------------------
*/

export const fetchPlatformSubscriptionPlanById =
    createAsyncThunk(
        "platformSubscriptionPlan/details",

        async (
            id,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.get(
                    config
                        .platformSubscriptionPlanById(
                            id
                        )
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| CREATE
|--------------------------------------------------------------------------
*/

export const createPlatformSubscriptionPlan =
    createAsyncThunk(
        "platformSubscriptionPlan/create",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.post(
                    config.platformSubscriptionPlans,
                    payload
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| UPDATE
|--------------------------------------------------------------------------
*/

export const updatePlatformSubscriptionPlan =
    createAsyncThunk(
        "platformSubscriptionPlan/update",

        async (
            {
                id,
                data,
            },
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.put(
                    config
                        .platformSubscriptionPlanById(
                            id
                        ),

                    data
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

export const updatePlatformSubscriptionPlanStatus =
    createAsyncThunk(
        "platformSubscriptionPlan/status",

        async (
            {
                id,
                status,
            },
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.patch(
                    config
                        .platformSubscriptionPlanStatus(
                            id
                        ),

                    {
                        status,
                    }
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| DELETE
|--------------------------------------------------------------------------
*/

export const deletePlatformSubscriptionPlan =
    createAsyncThunk(
        "platformSubscriptionPlan/delete",

        async (
            id,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.delete(
                    config
                        .platformSubscriptionPlanById(
                            id
                        )
                );


            if (
                !response?.success
            ) {

                return rejectWithValue(
                    response
                );

            }


            return {
                response,
                id,
            };

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformSubscriptionPlanSlice =
    createSlice({

        name:
            "platformSubscriptionPlan",

        initialState,

        reducers: {

            clearSelectedSubscriptionPlan:
                (
                    state
                ) => {

                    state.selectedPlan =
                        null;

                },

        },

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | DYNAMIC MODULES
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformSubscriptionModules.pending,
                        (
                            state
                        ) => {

                            state.modulesLoading =
                                true;

                            state.modulesError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionModules.fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.modulesLoading =
                                false;

                            state.modules =
                                action
                                    .payload
                                    ?.data
                                    ?.modules ||
                                [];

                            state.legacyModuleCodeMap =
                                action
                                    .payload
                                    ?.data
                                    ?.legacyModuleCodeMap ||
                                {};

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionModules.rejected,
                        (
                            state,
                            action
                        ) => {

                            state.modulesLoading =
                                false;

                            state.modulesError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load subscription modules.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | SYNC TENANT PLAN ACCESS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        syncPlatformTenantPlanAccess.pending,
                        (
                            state
                        ) => {

                            state.syncingTenants =
                                true;

                            state.syncTenantsError =
                                null;

                            state.syncTenantsResult =
                                null;

                        }
                    )
                    .addCase(
                        syncPlatformTenantPlanAccess.fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.syncingTenants =
                                false;

                            state.syncTenantsResult =
                                action
                                    .payload
                                    ?.data ||
                                null;

                        }
                    )
                    .addCase(
                        syncPlatformTenantPlanAccess.rejected,
                        (
                            state,
                            action
                        ) => {

                            state.syncingTenants =
                                false;

                            state.syncTenantsError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to synchronize tenant plan access.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | LIST
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformSubscriptionPlans.pending,
                        (
                            state
                        ) => {

                            state.loading =
                                true;

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionPlans.fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.loading =
                                false;

                            state.plans =
                                action
                                    .payload
                                    ?.data ||
                                [];

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionPlans.rejected,
                        (
                            state,
                            action
                        ) => {

                            state.loading =
                                false;

                            state.error =
                                action.payload;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DETAILS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformSubscriptionPlanById.pending,
                        (
                            state
                        ) => {

                            state.detailsLoading =
                                true;

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionPlanById.fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.selectedPlan =
                                action
                                    .payload
                                    ?.data ||
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformSubscriptionPlanById.rejected,
                        (
                            state
                        ) => {

                            state.detailsLoading =
                                false;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | CREATE / UPDATE
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        createPlatformSubscriptionPlan.pending,
                        (
                            state
                        ) => {

                            state.saving =
                                true;

                        }
                    )
                    .addCase(
                        createPlatformSubscriptionPlan.fulfilled,
                        (
                            state
                        ) => {

                            state.saving =
                                false;

                        }
                    )
                    .addCase(
                        createPlatformSubscriptionPlan.rejected,
                        (
                            state
                        ) => {

                            state.saving =
                                false;

                        }
                    )

                    .addCase(
                        updatePlatformSubscriptionPlan.pending,
                        (
                            state
                        ) => {

                            state.saving =
                                true;

                        }
                    )
                    .addCase(
                        updatePlatformSubscriptionPlan.fulfilled,
                        (
                            state
                        ) => {

                            state.saving =
                                false;

                        }
                    )
                    .addCase(
                        updatePlatformSubscriptionPlan.rejected,
                        (
                            state
                        ) => {

                            state.saving =
                                false;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DELETE
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        deletePlatformSubscriptionPlan.pending,
                        (
                            state
                        ) => {

                            state.deleting =
                                true;

                        }
                    )
                    .addCase(
                        deletePlatformSubscriptionPlan.fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.deleting =
                                false;

                            state.plans =
                                state.plans.filter(
                                    (
                                        plan
                                    ) =>
                                        String(
                                            plan?._id
                                        ) !==
                                        String(
                                            action
                                                .payload
                                                ?.id
                                        )
                                );

                        }
                    )
                    .addCase(
                        deletePlatformSubscriptionPlan.rejected,
                        (
                            state
                        ) => {

                            state.deleting =
                                false;

                        }
                    );

            },

    });


export const {
    clearSelectedSubscriptionPlan,
} =
    platformSubscriptionPlanSlice.actions;


export const selectPlatformSubscriptionPlans =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.plans ||
        [];


export const selectPlatformSubscriptionPlanLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformSubscriptionPlan
                ?.loading
        );


export const selectPlatformSubscriptionPlanSaving =
    (
        state
    ) =>
        Boolean(
            state
                .platformSubscriptionPlan
                ?.saving
        );


export const selectPlatformSubscriptionModules =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.modules ||
        [];


export const selectPlatformSubscriptionLegacyModuleCodeMap =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.legacyModuleCodeMap ||
        {};


export const selectPlatformSubscriptionModulesLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformSubscriptionPlan
                ?.modulesLoading
        );


export const selectPlatformSubscriptionModulesError =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.modulesError ||
        null;


export const selectPlatformTenantPlanSyncing =
    (
        state
    ) =>
        Boolean(
            state
                .platformSubscriptionPlan
                ?.syncingTenants
        );


export const selectPlatformTenantPlanSyncError =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.syncTenantsError ||
        null;


export const selectPlatformTenantPlanSyncResult =
    (
        state
    ) =>
        state
            .platformSubscriptionPlan
            ?.syncTenantsResult ||
        null;


export default
    platformSubscriptionPlanSlice.reducer;